import React, { useState, useEffect } from 'react';
import API from '../../utils/api';
import {
  FiCalendar,
  FiCheckCircle, FiXCircle, FiClock
} from 'react-icons/fi';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import DashboardLayout from '../../components/DashboardLayout';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalBookings: 0,
    completedBookings: 0,
    cancelledBookings: 0,
    pendingBookings: 0,
    totalCustomers: 0,
    totalMechanics: 0,
    averageServiceTime: 0
  });

  const [timeRange, setTimeRange] = useState('weekly');
  const [bookingData, setBookingData] = useState([]);
  const [mechanicPerformance, setMechanicPerformance] = useState([]);
  const [servicePopularity, setServicePopularity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch dashboard statistics
        try {
          const statsResponse = await API.get('/api/dashboard/stats');
          if (statsResponse.data) {
            setStats({
              totalBookings: statsResponse.data.totalBookings || 0,
              completedBookings: statsResponse.data.completedBookings || 0,
              cancelledBookings: statsResponse.data.cancelledBookings || 0,
              pendingBookings: statsResponse.data.pendingBookings || 0,
              totalCustomers: statsResponse.data.totalCustomers || 0,
              totalMechanics: statsResponse.data.totalMechanics || 0,
              averageServiceTime: statsResponse.data.averageServiceTime || 0
            });
          }
        } catch (err) {
          console.error('Error fetching dashboard stats:', err);
          setError('Failed to load dashboard statistics');
        }

        // Fetch booking data based on time range
        try {
          let bookingEndpoint;
          switch (timeRange) {
            case 'daily':
              bookingEndpoint = '/api/dashboard/bookings-by-day';
              break;
            case 'monthly':
              bookingEndpoint = '/api/dashboard/bookings-by-month';
              break;
            default: // weekly
              bookingEndpoint = '/api/dashboard/bookings-by-week';
          }

          const bookingResponse = await API.get(bookingEndpoint);
          if (bookingResponse.data) {
            setBookingData(bookingResponse.data);
          }
        } catch (err) {
          console.error(`Error fetching ${timeRange} booking data:`, err);
          setError(`Failed to load ${timeRange} booking data`);
          setBookingData([]);
        }

        // Fetch mechanic performance data
        try {
          const mechanicResponse = await API.get('/api/dashboard/mechanic-performance');
          if (mechanicResponse.data) {
            setMechanicPerformance(mechanicResponse.data);
          }
        } catch (err) {
          console.error('Error fetching mechanic performance data:', err);
          setError('Failed to load mechanic performance data');
          setMechanicPerformance([]);
        }

        // Fetch service popularity data
        try {
          const serviceResponse = await API.get('/api/dashboard/service-stats');
          if (serviceResponse.data) {
            setServicePopularity(serviceResponse.data);
          }
        } catch (err) {
          console.error('Error fetching service popularity data:', err);
          setError('Failed to load service popularity data');
          setServicePopularity([]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [timeRange]);

  // Colors for charts
  const COLORS = ['#FF6B00', '#1A1A1A', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800'];

  const StatCard = ({ icon, title, value, color }) => (
    <div className="bg-white rounded-lg shadow-md p-6 flex items-center">
      <div className={`rounded-full p-3 mr-4 ${color}`}>
        {icon}
      </div>
      <div>
        <h3 className="text-gray-500 text-sm font-medium">{title}</h3>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout userRole="owner" userName="Owner">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="owner" userName="Owner">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole="owner" userName="Owner">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Dashboard Laporan Bisnis</h1>
        <p className="text-gray-600">Statistik dan performa bisnis XSignature Auto Garage</p>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6 flex space-x-2">
        <button
          onClick={() => setTimeRange('daily')}
          className={`px-4 py-2 rounded-md ${timeRange === 'daily' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Harian
        </button>
        <button
          onClick={() => setTimeRange('weekly')}
          className={`px-4 py-2 rounded-md ${timeRange === 'weekly' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Mingguan
        </button>
        <button
          onClick={() => setTimeRange('monthly')}
          className={`px-4 py-2 rounded-md ${timeRange === 'monthly' ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'}`}
        >
          Bulanan
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={<FiCalendar className="text-white" size={20} />}
          title="Total Booking"
          value={stats.totalBookings || 0}
          color="bg-primary"
        />
        <StatCard
          icon={<FiCheckCircle className="text-white" size={20} />}
          title="Booking Selesai"
          value={stats.completedBookings || 0}
          color="bg-green-500"
        />
        <StatCard
          icon={<FiXCircle className="text-white" size={20} />}
          title="Booking Batal"
          value={stats.cancelledBookings || 0}
          color="bg-red-500"
        />
        <StatCard
          icon={<FiClock className="text-white" size={20} />}
          title="Rata-rata Waktu (jam)"
          value={(stats.averageServiceTime || 0).toFixed(1)}
          color="bg-blue-500"
        />
      </div>

      {/* Booking Trends Chart */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Tren Booking {timeRange === 'daily' ? 'Harian' : timeRange === 'weekly' ? 'Mingguan' : 'Bulanan'}</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={bookingData || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="bookings" stroke="#FF6B00" activeDot={{ r: 8 }} name="Jumlah Booking" />
            <Line type="monotone" dataKey="completed" stroke="#4CAF50" name="Selesai" />
            <Line type="monotone" dataKey="cancelled" stroke="#F44336" name="Batal" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Mechanic Performance Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Performa Montir</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={mechanicPerformance || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="jobs" fill="#4CAF50" name="Booking Selesai" />
              <Bar dataKey="rating" fill="#2196F3" name="Rating" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Service Popularity Chart */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Layanan Terpopuler</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={servicePopularity || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {(servicePopularity || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard; 